import React, { useState } from "react";
import axios from "axios";
import { validateIndianMobile, PHONE_ERROR_MESSAGES } from "../utils/phone";
import { trackCompleteRegistration } from "../utils/metaPixel";

/**
 * Inline phone capture for Home hero
 * Fast-lane alternative to full modal
 */
const InlinePhoneCapture = () => {
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate Indian mobile number (on submit only)
    const { valid, normalized, reason } = validateIndianMobile(phone);
    if (!valid) {
      setError(PHONE_ERROR_MESSAGES[reason] || PHONE_ERROR_MESSAGES.invalid);
      return;
    }

    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
      
      await axios.post(`${backendUrl}/api/sample-requests`, {
        phone_number: normalized,
        name: null,
        city: null,
        lots_of_interest: [],
        source: "hero_inline"
      });

      // Google Ads conversion tracking - silently push /thank-you to the URL
      // so the conversion tag fires. No visible navigation; UI stays on home.
      try {
        if (typeof window !== "undefined" && window.history && window.history.pushState) {
          window.history.pushState({}, "", "/thank-you");
        }
      } catch (_) {
        // no-op - tracking should never break submission flow
      }

      // Meta Pixel conversion tracking - fired on backend success
      trackCompleteRegistration();

      setIsSuccess(true);
    } catch (err) {
      console.error("Failed to submit phone:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div
        data-testid="inline-phone-success"
        className="animate-fade-in"
      >
        <p className="font-serif italic text-base md:text-lg text-brand-cream/75 text-center">
          Got it. We&apos;ll WhatsApp you shortly.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="inline-phone-capture" className="animate-fade-in">
      {/* Prompt */}
      <p className="font-sans italic text-sm md:text-base text-brand-cream/55 text-center mb-4">
        Or just drop your number - we&apos;ll WhatsApp you with our catalogue.
      </p>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 max-w-md mx-auto"
      >
        {/* Phone Input */}
        <div className="flex-1 relative">
          <input
            type="tel"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setError("");
            }}
            placeholder="+91 98XXX XXXXX"
            disabled={isSubmitting}
            maxLength={15}
            inputMode="tel"
            autoComplete="tel"
            data-testid="inline-phone-input"
            className="w-full bg-transparent text-brand-cream font-sans text-base px-0 py-2.5 border-0 border-b border-brand-cream/30 focus:border-brand-gold/60 focus:outline-none transition-colors placeholder:text-brand-cream/30 disabled:opacity-50"
          />
          {error && (
            <p
              data-testid="inline-phone-error"
              className="absolute -bottom-6 left-0 font-sans text-xs text-red-400/80"
            >
              {error}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          data-testid="inline-phone-submit"
          className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-brand-gold text-brand-charcoal font-sans text-sm font-medium uppercase tracking-[0.2em] transition-all duration-300 hover:bg-brand-gold/90 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
          style={{ borderRadius: "5px" }}
        >
          {isSubmitting ? "Sending..." : "Send →"}
        </button>
      </form>
    </div>
  );
};

export default InlinePhoneCapture;
